import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useBrowserSTT
 *
 * Wraps the browser's native Web Speech API (SpeechRecognition).
 * In Chrome/Edge this streams to Google/Microsoft's cloud STT in real-time —
 * sub-second latency, excellent Indian English accuracy with lang="en-IN".
 *
 * Exposes the same surface as useWhisperSTT so RealtimeConversation needs
 * no structural changes:
 *   { transcript, liveText, isListening, startListening, stopListening, resetTranscript }
 */
export default function useBrowserSTT({
    maxSilenceMs     = 4000,
    onSilenceTimeout = null,
    onError          = null,
} = {}) {
    const [transcript,  setTranscript]  = useState('');
    const [liveText,    setLiveText]    = useState('');
    const [isListening, setIsListening] = useState(false);

    const recognitionRef     = useRef(null);
    const transcriptRef      = useRef('');
    const finalRef           = useRef('');          // accumulated final segments
    const silenceTimerRef    = useRef(null);
    const lastSpeechTimeRef  = useRef(Date.now());
    const stoppedRef         = useRef(false);
    const onSilenceRef       = useRef(onSilenceTimeout);
    const onErrorRef         = useRef(onError);

    useEffect(() => { onSilenceRef.current  = onSilenceTimeout; }, [onSilenceTimeout]);
    useEffect(() => { onErrorRef.current    = onError;          }, [onError]);

    const stopListening = useCallback(() => {
        stoppedRef.current = true;
        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        try { recognitionRef.current?.stop(); } catch {}
        recognitionRef.current = null;
        setIsListening(false);
    }, []);

    const resetTranscript = useCallback(() => {
        transcriptRef.current = '';
        finalRef.current      = '';
        setTranscript('');
        setLiveText('');
    }, []);

    const startListening = useCallback(async () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            throw new Error(
                'Web Speech API not available. Use Chrome or Edge for real-time STT.'
            );
        }

        // Web Speech API requires HTTPS outside of localhost
        const host = window.location.hostname;
        const isSecure = window.location.protocol === 'https:' ||
                         host === 'localhost' || host === '127.0.0.1';
        if (!isSecure) {
            throw new Error(
                'Microphone requires HTTPS. Your site is on HTTP — switch to the HTTPS URL.'
            );
        }

        // Clean up any previous session
        stoppedRef.current       = false;
        transcriptRef.current    = '';
        finalRef.current         = '';
        lastSpeechTimeRef.current = Date.now();

        if (silenceTimerRef.current) {
            clearInterval(silenceTimerRef.current);
            silenceTimerRef.current = null;
        }
        try { recognitionRef.current?.stop(); } catch {}

        const recognition = new SR();
        recognitionRef.current = recognition;

        recognition.continuous      = true;
        recognition.interimResults  = true;
        recognition.lang            = 'en-IN'; // Indian English — best accuracy for Indian accent
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
            if (stoppedRef.current) return;

            let interim = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const r = event.results[i];
                if (r.isFinal) {
                    finalRef.current += r[0].transcript + ' ';
                } else {
                    interim += r[0].transcript;
                }
            }

            const combined = (finalRef.current + interim).trim();
            transcriptRef.current = combined;
            setTranscript(combined);  // drives latestTranscriptRef in RealtimeConversation
            setLiveText(combined);
            lastSpeechTimeRef.current = Date.now();
        };

        recognition.onerror = (event) => {
            if (event.error === 'aborted') return;
            if (event.error === 'no-speech')  return; // normal — silence timeout handled separately

            let msg;
            if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                msg = 'Microphone blocked. Allow mic permission or check that the page is on HTTPS.';
            } else if (event.error === 'network') {
                msg = 'Speech recognition network error — check internet connection.';
            } else {
                msg = `Speech recognition error: ${event.error}`;
            }
            console.error('[BrowserSTT]', msg);
            onErrorRef.current?.(msg);
        };

        recognition.onend = () => {
            // Continuous mode can stop unexpectedly; restart unless we called stopListening
            if (!stoppedRef.current && recognitionRef.current) {
                try { recognition.start(); } catch {}
            }
        };

        recognition.start();
        setIsListening(true);

        // Silence watchdog: fire onSilenceTimeout after maxSilenceMs of quiet WITH speech
        silenceTimerRef.current = setInterval(() => {
            if (stoppedRef.current) return;
            const silenceMs = Date.now() - lastSpeechTimeRef.current;
            const text      = transcriptRef.current;
            if (silenceMs >= maxSilenceMs && text) {
                stopListening();
                onSilenceRef.current?.(text);
            }
        }, 200);
    }, [maxSilenceMs, stopListening]);

    // Cleanup on unmount
    useEffect(() => () => stopListening(), [stopListening]);

    return { transcript, liveText, isListening, startListening, stopListening, resetTranscript };
}
